import React from 'react';
import {
  Box,
  Typography,
  Button,
  Fade,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getLogoMode } from '../../../utils';
import { useWhitelabel } from '../../../context/WhitelabelContext';

const WelcomeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
}));

interface WelcomeStepProps {
  appName: string;
  userName?: string;
  onNext: () => void;
}

export default function WelcomeStep({ appName, userName, onNext }: WelcomeStepProps) {
  const theme = useTheme();
  const { whitelabelLogos } = useWhitelabel();
  
  // Extract first name from display name or email
  const firstName = userName 
    ? userName.split(' ')[0] 
    : 'there';
  
  // Get the appropriate logo
  const getLogo = () => {
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(theme.palette.mode);
  };

  return (
    <WelcomeContainer>
      <Fade in={true} timeout={1000}>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ mb: 4 }}>
            <img
              src={getLogo()}
              alt={appName}
              style={{
                maxHeight: 50,
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>

          <Typography 
            variant="h4" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            Hi {firstName}! 👋
          </Typography>

          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: 4, 
              maxWidth: 500,
              lineHeight: 1.8
            }}
          >
            We're excited to help you build your AI-powered workforce. 
            Let's get you set up in just a few quick steps.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            <Button
              variant="contained"
              size="large"
              onClick={onNext}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 12px rgba(255, 255, 255, 0.1)'
                  : '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 6px 20px rgba(255, 255, 255, 0.15)'
                    : '0 6px 20px rgba(0, 0, 0, 0.2)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              Let's Get Started
            </Button>

            <Typography 
              variant="caption" 
              color="text.secondary" 
            >
              This will only take 2-3 minutes
            </Typography>
          </Box>
        </Box>
      </Fade>
    </WelcomeContainer>
  );
}